<template>
  <div class="chat-list">
    <div v-if="searchVisible" class="chat-list__search">
      <i class="fas fa-search" aria-hidden="true"></i>
      <input ref="searchInput" v-model="searchQuery" type="search" :placeholder="labels.search_placeholder">
      <button type="button" :aria-label="labels.search_clear" :disabled="searchQuery === ''" @click="clearSearch"><i class="fas fa-times" aria-hidden="true"></i></button>
    </div>
    <div class="chat-list__ribbon">
      <div ref="categoryStrip" class="chat-list__categories" role="tablist">
        <button v-for="category in categories" :key="categoryKey(category)" type="button" class="chat-list__category" :class="{'chat-list__category--active': categoryKey(category) === selectedCategoryKey}" role="tab" :aria-selected="categoryKey(category) === selectedCategoryKey" @click="selectCategory(category)">{{ category.name }}</button>
      </div>
      <button ref="menuTrigger" class="chat-list__menu" type="button" :aria-label="labels.menu" aria-haspopup="menu" :aria-expanded="menuOpen" @click="toggleMenu"><i class="fas fa-ellipsis-h" aria-hidden="true"></i></button>
    </div>

    <div class="chat-list__content" role="tabpanel" :aria-busy="currentState.isLoading" @touchstart="handleTouchStart" @touchend="handleTouchEnd">
      <Transition :name="slideTransitionName">
        <div :key="selectedCategoryKey" class="chat-list__panel">
          <div v-if="currentState.isLoading && currentState.chats.length === 0" class="chat-list__loading"><span class="ec-loading__spinnerVisual" aria-hidden="true"></span></div>
          <div v-else-if="currentState.hasError && currentState.chats.length === 0" class="chat-list__status" role="alert">{{ labels.error }}</div>
          <div v-else-if="filteredChats.length > 0" class="chat-list__items">
            <ChatListRow v-for="chat in filteredChats" :key="chat.public_customer_id" :chat="chat" :chat-url="chatUrl(chat.public_customer_id)" :highlighted="activeHighlightedId === String(chat.public_customer_id)" :membership-mode="membershipMode" :selected="editedMemberIds.has(String(chat.public_customer_id))" @open="$emit('open-chat', chat.public_customer_id)" @toggle="toggleMember(chat.public_customer_id)" />
          </div>
          <div v-else class="chat-list__empty">
            <template v-if="searchQuery !== ''"><p>{{ labels.search_empty }}</p></template>
            <template v-else-if="selectedCategory.category_type === 2"><p>{{ labels.empty_unread }}</p></template>
            <template v-else-if="selectedCategory.category_type === 3"><p>{{ labels.empty_saioshi_title }}<br>{{ labels.empty_saioshi_description }}</p></template>
            <template v-else-if="selectedCategory.category_type === 1"><p>{{ labels.empty_title }}<br>{{ labels.empty_description }}</p><a class="chat-list__matching-button" :href="initialData.matching_url">{{ labels.matching }}</a></template>
            <template v-else-if="selectedCategory.category_type === 0"><p>{{ labels.category_members_empty }}</p></template>
          </div>
          <div v-if="currentState.isLoading && currentState.chats.length > 0" class="chat-list__more" role="status">{{ labels.loading }}</div>
          <div v-else-if="currentState.hasError && currentState.chats.length > 0" class="chat-list__append-error" role="alert"><p><i class="fas fa-triangle-exclamation" aria-hidden="true"></i>{{ labels.error }}</p><p>{{ labels.reload_after_error }}</p></div>
        </div>
      </Transition>
    </div>

    <div v-if="membershipMode" class="chat-list__member-footer">
      <button class="chat-list__member-help" type="button" @click="modal = 'help'">{{ labels.category_help }} <i class="far fa-question-circle" aria-hidden="true"></i></button>
      <div class="chat-list__member-actions"><button type="button" @click="cancelMembers">{{ labels.cancel }}</button><button type="button" :disabled="saving" @click="saveMembers">{{ labels.category_change }}</button></div>
    </div>
    <div v-if="membershipMode" class="chat-list__member-footer-spacer" aria-hidden="true"></div>

    <Teleport to="body">
      <div v-if="menuOpen" class="chat-list-menu" @click.self="closeMenu" @keydown.esc="closeMenu">
        <div class="chat-list-menu__panel" role="menu" :aria-label="labels.menu">
          <button type="button" role="menuitem" @click="toggleSearch">{{ searchVisible ? labels.search_hide : labels.search_show }}</button>
          <button type="button" role="menuitem" @click="openManagement">{{ labels.category_management }}</button>
          <button type="button" role="menuitem" @click="openUploads">{{ labels.menu_uploads }}</button>
          <button type="button" role="menuitem" @click="openBlockList">{{ labels.menu_block_list }}</button>
          <button v-if="initialData.chat_read_preference_url" class="chat-menu-toggle" type="button" role="menuitemcheckbox" :aria-checked="!!store.readDisabled" :disabled="preferenceSaving" @click="toggleReadPreference">
            <span>{{ labels.read_disabled }}</span><span class="chat-menu-toggle__switch" :class="{'chat-menu-toggle__switch--on': store.readDisabled}" aria-hidden="true"></span>
          </button>
          <button v-if="initialData.is_oshi_customer" type="button" role="menuitem" @click="openMessageBulk">{{ labels.menu_message_bulk }}</button>
        </div>
      </div>
      <div v-if="modal !== null" class="chat-category-modal" @click.self="closeModal" @keydown.esc="closeModal">
        <section class="chat-category-modal__panel" role="dialog" aria-modal="true">
          <header>
            <button v-if="hasModalBack" ref="categoryModalBack" class="chat-category-modal__back" type="button" :aria-label="labels.back" @click="backToManagement"><i class="fas fa-chevron-left" aria-hidden="true"></i></button>
            <span v-else class="chat-category-modal__header-spacer" aria-hidden="true"></span>
            <h2>{{ modalTitle }}</h2>
            <button ref="categoryModalClose" class="chat-category-modal__close" type="button" :aria-label="labels.close" @click="closeModal"><i class="fas fa-times" aria-hidden="true"></i></button>
          </header>
          <div class="chat-category-modal__body">
            <div v-if="modal === 'management'" class="chat-category-modal__menu">
              <button type="button" @click="openCreate">{{ labels.category_create }}</button>
              <button type="button" @click="openDisplay">{{ labels.category_display }}</button>
              <button type="button" :disabled="customCategories.length === 0" @click="openEdit">{{ labels.category_edit }}</button>
              <button type="button" :disabled="customCategories.length === 0" @click="openMemberSelect">{{ labels.category_members }}</button>
            </div>
            <form v-else-if="modal === 'create'" @submit.prevent="createCategory"><label><span class="chat-category-modal__label"><span>{{ labels.category_name }}</span><small>{{ labels.category_name_limit }}</small></span><input v-model="categoryName" maxlength="24" required></label><div class="chat-category-modal__footer"><button class="ec-inlineBtn--action chat-category-modal__primary chat-category-modal__primary--standard" type="submit" :disabled="saving">{{ labels.category_register }}</button></div></form>
            <div v-else-if="modal === 'display'">
              <div ref="sortableList" class="chat-category-sortable">
                <div v-for="setting in displayDraft" :key="categoryKey(setting)" class="chat-category-sortable__row"><i class="fas fa-bars chat-category-sortable__handle"></i><span role="button" :tabindex="setting.category_type === 1 ? -1 : 0" @click="toggleDisplaySetting(setting)" @keydown.enter.prevent="toggleDisplaySetting(setting)" @keydown.space.prevent="toggleDisplaySetting(setting)">{{ setting.name }}</span><input v-model="setting.display_flg" type="checkbox" :disabled="setting.category_type === 1"></div>
              </div>
              <div class="chat-category-modal__footer"><button class="ec-inlineBtn--action chat-category-modal__primary chat-category-modal__primary--standard" type="button" :disabled="saving" @click="saveDisplay">{{ labels.category_change }}</button></div>
            </div>
            <div v-else-if="modal === 'edit'" class="chat-category-edit">
              <div v-for="category in customCategories" :key="category.chat_category_id"><button type="button" @click="editCategoryId = editCategoryId === category.chat_category_id ? null : category.chat_category_id">{{ category.name }}</button><form v-if="editCategoryId === category.chat_category_id" @submit.prevent="renameCategory(category)"><input v-model="editNames[category.chat_category_id]" maxlength="24" required><div><button type="button" @click="confirmDeleteId = category.chat_category_id">{{ labels.category_delete }}</button><button type="submit" :disabled="saving">{{ labels.category_change }}</button></div></form></div>
            </div>
            <div v-else-if="modal === 'member-select'" class="chat-category-modal__menu"><button v-for="category in customCategories" :key="category.chat_category_id" type="button" :disabled="loadingMembers" @click="startMembers(category)">{{ category.name }}</button></div>
            <div v-else-if="modal === 'help'" class="chat-category-help"><img :src="initialData.category_help_image_path" alt=""><p>{{ labels.category_help_description }}</p></div>
          </div>
        </section>
      </div>
      <div v-if="confirmDeleteId !== null" class="chat-category-confirm" role="dialog" aria-modal="true"><div><p>{{ labels.category_delete_confirm }}</p><button type="button" @click="confirmDeleteId = null">{{ labels.cancel }}</button><button type="button" :disabled="saving" @click="deleteCategory">{{ labels.category_delete }}</button></div></div>
    </Teleport>
    <ChatUploadManager ref="uploadManager" :labels="labels" :list-api-url="initialData.chat_upload_list_api_url" :delete-api-url="initialData.chat_upload_delete_api_url" :delete-csrf-token="initialData.upload_delete_csrf_token" :delete-limit="initialData.upload_delete_limit" @notify="$emit('notify', $event)" />
    <ChatBlockListManager v-if="blockListCreated" ref="blockListManager" :labels="labels" :list-api-url="initialData.chat_block_list_api_url" :unblock-api-url="initialData.chat_block_list_unblock_api_url" :unblock-csrf-token="initialData.block_list_unblock_csrf_token" :unblock-limit="initialData.block_list_unblock_limit" @notify="$emit('notify', $event)" />
    <ChatMessageBulkManager v-if="messageBulkCreated" ref="messageBulkManager" :initial-data="initialData" :labels="labels" @notify="$emit('notify', $event)" />
  </div>
</template>

<script setup>
import {computed, nextTick, onActivated, onBeforeUnmount, onDeactivated, onMounted, ref, watch} from 'vue';
import Sortable from 'sortablejs';
import ChatListRow from './ChatListRow.vue';
import ChatBlockListManager from './ChatBlockListManager.vue';
import ChatMessageBulkManager from './ChatMessageBulkManager.vue';
import ChatUploadManager from './ChatUploadManager.vue';

const emit = defineEmits(['open-chat', 'notify']);

const props = defineProps({
  initialData: {
    type: Object,
    required: true,
  },
  store: {
    type: Object,
    required: true,
  },
  highlightedPublicCustomerId: {
    type: String,
    default: null,
  },
  chatLoader: {
    type: Function,
    default: null,
  },
  updateLoader: {type: Function, default: null},
});

const categoryKey = (category) => category.chat_category_id === null
  ? `system-${category.category_type}`
  : `custom-${category.chat_category_id}`;
const categoryData = ref([...(props.initialData.categories ?? [])]);
const categorySettings = ref([...(props.initialData.category_settings ?? props.initialData.categories ?? [])]);
const categories = computed(() => [...categoryData.value].sort((left, right) => left.sort_no - right.sort_no));
const labels = computed(() => props.initialData.labels);
const preferenceSaving = ref(false);
const toggleReadPreference = async () => {
  if (preferenceSaving.value) return;
  preferenceSaving.value = true;
  const enabled = !props.store.readDisabled;
  try {
    const response = await $.ajax({
      url: props.initialData.chat_read_preference_url,
      method: 'PUT', contentType: 'application/json', dataType: 'json',
      data: JSON.stringify({enabled, _token: props.initialData.chat_preferences_csrf_token}),
    });
    if (response?.status !== 'OK') throw new Error('Invalid preference response');
    props.store.readDisabled = enabled;
  } catch (error) {
    emit('notify', {type: 'error', message: error?.responseJSON?.message ?? labels.value.error});
  } finally {
    preferenceSaving.value = false;
  }
};
const initialCategory = categories.value.find((category) => (
  category.category_type === props.initialData.selected_category_type
  && category.chat_category_id === props.initialData.selected_chat_category_id
)) ?? categories.value[0];
if (props.store.list.selectedCategoryKey === null) {
  props.store.list.selectedCategoryKey = categoryKey(initialCategory);
}
const categoryStates = props.store.list.categoryStates;
const activeHighlightedId = ref(props.highlightedPublicCustomerId === null ? null : String(props.highlightedPublicCustomerId));
const slideDirection = ref('next');
let highlightTimer = null;
let touchStart = null;
let sortable = null;
const menuTrigger = ref(null);
const categoryModalClose = ref(null);
const categoryModalBack = ref(null);
const searchInput = ref(null);
const categoryStrip = ref(null);
const uploadManager = ref(null);
const blockListManager = ref(null);
const blockListCreated = ref(false);
const messageBulkManager = ref(null);
const messageBulkCreated = ref(false);
const sortableList = ref(null);
const menuOpen = ref(false);
const modal = ref(null);
const saving = ref(false);
const loadingMembers = ref(false);
const categoryName = ref('');
const displayDraft = ref([]);
const editCategoryId = ref(null);
const editNames = ref({});
const confirmDeleteId = ref(null);
const membershipMode = ref(false);
const memberCategory = ref(null);
const originalMemberIds = ref(new Set());
const editedMemberIds = ref(new Set());
const SEARCH_STORAGE_KEY = 'chat-list-search-visible';
const searchVisible = ref(window.localStorage.getItem(SEARCH_STORAGE_KEY) === '1');
const searchQuery = ref('');
let updateTimer = null;
let updateRequest = null;
let updateListening = false;
props.store.list.updateCursor ??= String(props.initialData.chat_update_cursor ?? '0');

const createCategoryState = () => ({
  chats: [],
  nextCursor: null,
  hasNext: false,
  isLoaded: false,
  isLoading: false,
  loadRequest: null,
  hasError: false,
  scrollTop: 0,
});

categorySettings.value.forEach((category) => {
  categoryStates[categoryKey(category)] ??= createCategoryState();
});

const selectedCategoryKey = computed({
  get: () => props.store.list.selectedCategoryKey,
  set: (value) => { props.store.list.selectedCategoryKey = value; },
});
const selectedCategory = computed(() => categories.value.find((category) => categoryKey(category) === selectedCategoryKey.value));
const currentState = computed(() => categoryStates[selectedCategoryKey.value] ?? createCategoryState());
const filteredChats = computed(() => {
  const query = searchQuery.value.toLocaleLowerCase();
  return query === '' ? currentState.value.chats : currentState.value.chats.filter((chat) => String(chat.nickname ?? '').toLocaleLowerCase().includes(query));
});
const customCategories = computed(() => categorySettings.value.filter((category) => category.category_type === 0));
const modalTitle = computed(() => ({management: labels.value.category_management, create: labels.value.category_create, display: labels.value.category_display, edit: labels.value.category_edit, 'member-select': labels.value.category_members, help: labels.value.category_help}[modal.value] ?? ''));
const hasModalBack = computed(() => ['create', 'display', 'edit', 'member-select'].includes(modal.value));
const slideTransitionName = computed(() => `chat-list-slide-${slideDirection.value}`);
const chatUrl = (publicCustomerId) => props.initialData.chat_detail_url_template.replace(
  '%public_customer_id%',
  encodeURIComponent(String(publicCustomerId)),
);

const requestChats = async (category, cursor) => {
  const payload = {
    category_type: category.category_type,
    ...(category.chat_category_id === null ? {} : {chat_category_id: category.chat_category_id}),
    ...(cursor === null ? {} : {cursor}),
  };
  let response;
  if (props.chatLoader !== null) {
    response = await props.chatLoader(payload);
  } else {
    response = await $.ajax({
      url: props.initialData.chat_list_api_url,
      method: 'POST',
      contentType: 'application/json; charset=UTF-8',
      dataType: 'json',
      data: JSON.stringify(payload),
    });
  }

  if (response?.status !== 'OK' || response.result === null || typeof response.result !== 'object') {
    throw new Error('Invalid chat list response.');
  }

  return response.result;
};

const loadCategory = async (category, append = false) => {
  const key = categoryKey(category);
  const state = categoryStates[key];
  if (state === undefined || (!append && state.isLoaded) || (append && !state.hasNext)) {
    return;
  }
  if (state.isLoading) return state.loadRequest;

  state.isLoading = true;
  state.hasError = false;
  state.loadRequest = (async () => {
    try {
      const response = await requestChats(category, append ? state.nextCursor : null);
      state.chats = append ? [...state.chats, ...response.chats] : response.chats;
      state.nextCursor = response.next_cursor;
      state.hasNext = response.has_next;
      state.isLoaded = true;
    } catch (error) {
      state.hasError = true;
    } finally {
      state.isLoading = false;
      state.loadRequest = null;
    }
  })();
  return state.loadRequest;
};

const applyChatUpdate = async (chat) => {
  let selectedKey;
  while (true) {
    selectedKey = selectedCategoryKey.value;
    const selectedState = categoryStates[selectedKey];
    if (selectedState?.isLoading) await selectedState.loadRequest;
    if (selectedKey === selectedCategoryKey.value) break;
  }
  let selectedFound = false;
  Object.entries(categoryStates).forEach(([key, state]) => {
    if (!state.isLoaded) return;
    const index = state.chats.findIndex((item) => String(item.public_customer_id) === String(chat.public_customer_id));
    if (key !== selectedKey) {
      state.isLoaded = false;
      return;
    }
    if (index < 0) return;
    selectedFound = true;
    const current = state.chats.splice(index, 1)[0];
    Object.assign(current, chat);
    state.chats.unshift(current);
  });
  if (!selectedFound) {
    const state = categoryStates[selectedKey];
    if (state !== undefined) {
      state.isLoaded = false;
      state.chats = [];
      state.nextCursor = null;
      state.hasNext = false;
      await loadCategory(selectedCategory.value);
    }
  }
};

const requestUpdates = async (cursor) => {
  if (props.updateLoader !== null) return props.updateLoader(cursor);
  return $.ajax({
    url: props.initialData.chat_list_updates_api_url,
    method: 'POST',
    contentType: 'application/json; charset=UTF-8',
    dataType: 'json',
    data: JSON.stringify({cursor}),
  });
};

const fetchUpdates = async () => {
  if (updateRequest !== null) return updateRequest;
  updateRequest = (async () => {
    let hasNext = true;
    while (hasNext) {
      const response = await requestUpdates(props.store.list.updateCursor);
      if (!validResult(response) || !Array.isArray(response.result.updates)) throw new Error('Invalid chat list updates response.');
      for (const chat of response.result.updates) await applyChatUpdate(chat);
      props.store.list.updateCursor = String(response.result.next_cursor);
      hasNext = response.result.has_next === true;
    }
  })();
  try { await updateRequest; } catch (error) { /* 次回の30秒ポーリングで再試行する。 */ } finally { updateRequest = null; }
};

const scheduleUpdates = () => {
  if (!updateListening || props.store.webPushConfirmed || updateTimer !== null) return;
  updateTimer = window.setTimeout(async () => {
    updateTimer = null;
    await fetchUpdates();
    scheduleUpdates();
  }, 30000);
};

const startUpdatePolling = () => {
  if (updateListening || props.store.webPushConfirmed || typeof props.initialData.chat_list_updates_api_url !== 'string') return;
  updateListening = true;
  scheduleUpdates();
};

const stopUpdatePolling = () => {
  updateListening = false;
  if (updateTimer !== null) {
    window.clearTimeout(updateTimer);
    updateTimer = null;
  }
};

const selectCategory = async (category) => {
  const key = categoryKey(category);
  if (key === selectedCategoryKey.value || categoryStates[key] === undefined) {
    return;
  }

  currentState.value.scrollTop = window.scrollY;
  const currentIndex = categories.value.findIndex((item) => categoryKey(item) === selectedCategoryKey.value);
  const selectedIndex = categories.value.findIndex((item) => categoryKey(item) === key);
  slideDirection.value = selectedIndex > currentIndex ? 'next' : 'previous';
  selectedCategoryKey.value = key;
  await loadCategory(category);
  await nextTick();
  await scrollSelectedCategoryIntoView();
  window.scrollTo({top: currentState.value.scrollTop, behavior: 'instant'});
};

const scrollSelectedCategoryIntoView = async (behavior = 'smooth') => {
  await nextTick();
  const strip = categoryStrip.value;
  const active = strip?.querySelector('.chat-list__category--active');
  if (strip === null || active === null || typeof strip.scrollTo !== 'function') return;
  const left = active.offsetLeft;
  const right = left + active.offsetWidth;
  if (left < strip.scrollLeft) strip.scrollTo({left, behavior});
  else if (right > strip.scrollLeft + strip.clientWidth) strip.scrollTo({left: right - strip.clientWidth, behavior});
};

const request = (url, method, data) => $.ajax({url, method, contentType: 'application/json; charset=UTF-8', dataType: 'json', data: JSON.stringify(data)});
const validResult = (response) => response?.status === 'OK' && response.result !== null && typeof response.result === 'object';
const categoryUrl = (template, id) => template.replace('%chat_category_id%', encodeURIComponent(String(id)));
const notifyFailure = (error) => emit('notify', {type: 'error', message: error?.responseJSON?.message ?? labels.value.error});
const applyCategoryResponse = async (result) => {
  categoryData.value = [...result.categories];
  categorySettings.value = [...result.category_settings];
  categorySettings.value.forEach((category) => { categoryStates[categoryKey(category)] ??= createCategoryState(); });
  if (!categories.value.some((category) => categoryKey(category) === selectedCategoryKey.value)) {
    await selectCategory(categories.value.find((category) => category.category_type === 1));
  }
};
const closeMenu = async () => { menuOpen.value = false; await nextTick(); menuTrigger.value?.focus(); };
const toggleMenu = () => { menuOpen.value = !menuOpen.value; };
const toggleSearch = async () => {
  searchVisible.value = !searchVisible.value;
  window.localStorage.setItem(SEARCH_STORAGE_KEY, searchVisible.value ? '1' : '0');
  if (!searchVisible.value) searchQuery.value = '';
  await closeMenu();
};
const clearSearch = async () => {
  searchQuery.value = '';
  await nextTick();
  searchInput.value?.focus();
};
const openManagement = async () => {
  menuOpen.value = false;
  modal.value = 'management';
  await nextTick();
  categoryModalClose.value?.focus();
};
const openUploads = async () => { menuOpen.value = false; await uploadManager.value?.open(menuTrigger.value); };
const openBlockList = async () => {
  menuOpen.value = false;
  blockListCreated.value = true;
  await nextTick();
  await blockListManager.value?.open(menuTrigger.value);
};
const openMessageBulk = async () => {
  menuOpen.value = false;
  messageBulkCreated.value = true;
  await nextTick();
  await messageBulkManager.value?.open(menuTrigger.value);
};
const closeModal = async () => {
  sortable?.destroy();
  sortable = null;
  modal.value = null;
  categoryName.value = '';
  await nextTick();
  menuTrigger.value?.focus();
};
const backToManagement = async () => {
  sortable?.destroy();
  sortable = null;
  categoryName.value = '';
  editCategoryId.value = null;
  modal.value = 'management';
  await nextTick();
  categoryModalClose.value?.focus();
};
const focusModalBack = async () => {
  await nextTick();
  categoryModalBack.value?.focus();
};
const openCreate = async () => { categoryName.value = ''; modal.value = 'create'; await focusModalBack(); };
const openDisplay = async () => {
  displayDraft.value = categorySettings.value.map((category) => ({...category})).sort((a, b) => a.sort_no - b.sort_no);
  modal.value = 'display';
  await nextTick();
  categoryModalBack.value?.focus();
  sortable = Sortable.create(sortableList.value, {
    handle: '.chat-category-sortable__handle',
    animation: 150,
    onEnd: ({oldIndex, newIndex}) => {
      if (oldIndex === newIndex || oldIndex === undefined || newIndex === undefined) return;
      const reordered = [...displayDraft.value];
      const [item] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, item);
      displayDraft.value = reordered;
    },
  });
};
const openEdit = async () => {
  editNames.value = Object.fromEntries(customCategories.value.map((category) => [category.chat_category_id, category.name]));
  editCategoryId.value = null;
  modal.value = 'edit';
  await focusModalBack();
};
const openMemberSelect = async () => { modal.value = 'member-select'; await focusModalBack(); };
const toggleDisplaySetting = (setting) => {
  if (setting.category_type !== 1) setting.display_flg = !setting.display_flg;
};
const createCategory = async () => {
  if (saving.value) return;
  saving.value = true;
  try {
    const response = await request(props.initialData.chat_category_create_api_url, 'POST', {name: categoryName.value, _token: props.initialData.category_create_csrf_token});
    if (!validResult(response)) throw new Error('Invalid category response.');
    await applyCategoryResponse(response.result);
    closeModal();
    emit('notify', {type: 'success', message: labels.value.category_created});
  } catch (error) { notifyFailure(error); } finally { saving.value = false; }
};
const saveDisplay = async () => {
  if (saving.value) return;
  saving.value = true;
  try {
    const categoriesPayload = displayDraft.value.map((category) => ({category_type: category.category_type, chat_category_id: category.chat_category_id, display_flg: category.category_type === 1 ? true : category.display_flg}));
    const response = await request(props.initialData.chat_category_display_api_url, 'PUT', {categories: categoriesPayload, _token: props.initialData.category_display_csrf_token});
    if (!validResult(response)) throw new Error('Invalid category response.');
    await applyCategoryResponse(response.result);
    closeModal();
    emit('notify', {type: 'success', message: labels.value.category_display_saved});
  } catch (error) { notifyFailure(error); } finally { saving.value = false; }
};
const renameCategory = async (category) => {
  if (saving.value) return;
  saving.value = true;
  try {
    const response = await request(categoryUrl(props.initialData.chat_category_edit_api_url_template, category.chat_category_id), 'PUT', {name: editNames.value[category.chat_category_id], _token: props.initialData.category_edit_csrf_token});
    if (!validResult(response)) throw new Error('Invalid category response.');
    await applyCategoryResponse(response.result);
    openEdit();
    emit('notify', {type: 'success', message: labels.value.category_renamed});
  } catch (error) { notifyFailure(error); } finally { saving.value = false; }
};
const deleteCategory = async () => {
  if (saving.value || confirmDeleteId.value === null) return;
  saving.value = true;
  try {
    const deletedKey = `custom-${confirmDeleteId.value}`;
    const response = await request(categoryUrl(props.initialData.chat_category_edit_api_url_template, confirmDeleteId.value), 'DELETE', {_token: props.initialData.category_edit_csrf_token});
    if (!validResult(response)) throw new Error('Invalid category response.');
    await applyCategoryResponse(response.result);
    delete categoryStates[deletedKey];
    confirmDeleteId.value = null;
    customCategories.value.length === 0 ? closeModal() : openEdit();
    emit('notify', {type: 'success', message: labels.value.category_deleted});
  } catch (error) { notifyFailure(error); } finally { saving.value = false; }
};
const startMembers = async (category) => {
  if (loadingMembers.value) return;
  loadingMembers.value = true;
  try {
    const response = await request(categoryUrl(props.initialData.chat_category_members_api_url_template, category.chat_category_id), 'POST', {});
    if (!validResult(response) || !Array.isArray(response.result.public_customer_ids)) throw new Error('Invalid category members response.');
    memberCategory.value = category;
    originalMemberIds.value = new Set(response.result.public_customer_ids.map(String));
    editedMemberIds.value = new Set(originalMemberIds.value);
    membershipMode.value = true;
    closeModal();
    if (originalMemberIds.value.size === 0) await selectCategory(categories.value.find((item) => item.category_type === 1));
  } catch (error) { notifyFailure(error); } finally { loadingMembers.value = false; }
};
const toggleMember = (id) => {
  if (!membershipMode.value) return;
  const values = new Set(editedMemberIds.value);
  const key = String(id);
  values.has(key) ? values.delete(key) : values.add(key);
  editedMemberIds.value = values;
};
const hasMemberChanges = () => originalMemberIds.value.size !== editedMemberIds.value.size || [...originalMemberIds.value].some((id) => !editedMemberIds.value.has(id));
const cancelMembers = () => {
  if (hasMemberChanges() && !window.confirm(labels.value.category_cancel_confirm)) return;
  membershipMode.value = false;
  memberCategory.value = null;
  originalMemberIds.value = new Set();
  editedMemberIds.value = new Set();
};
const saveMembers = async () => {
  if (saving.value || memberCategory.value === null) return;
  saving.value = true;
  const added = [...editedMemberIds.value].filter((id) => !originalMemberIds.value.has(id));
  const removed = [...originalMemberIds.value].filter((id) => !editedMemberIds.value.has(id));
  try {
    const response = await request(categoryUrl(props.initialData.chat_category_members_api_url_template, memberCategory.value.chat_category_id), 'PUT', {add_public_customer_ids: added, remove_public_customer_ids: removed, _token: props.initialData.category_members_csrf_token});
    if (!validResult(response)) throw new Error('Invalid category members response.');
    const targetState = categoryStates[`custom-${memberCategory.value.chat_category_id}`];
    const chatsById = new Map();
    Object.values(categoryStates).forEach((state) => state.chats.forEach((chat) => chatsById.set(String(chat.public_customer_id), chat)));
    targetState.chats = targetState.chats.filter((chat) => !removed.includes(String(chat.public_customer_id)));
    added.forEach((id) => { if (!targetState.chats.some((chat) => String(chat.public_customer_id) === id) && chatsById.has(id)) targetState.chats.push(chatsById.get(id)); });
    const completedCategoryKey = `custom-${memberCategory.value.chat_category_id}`;
    const completedCategory = categories.value.find((category) => categoryKey(category) === completedCategoryKey);
    membershipMode.value = false;
    memberCategory.value = null;
    emit('notify', {type: 'success', message: labels.value.category_members_saved});
    await selectCategory(completedCategory ?? categories.value.find((category) => category.category_type === 1));
  } catch (error) { notifyFailure(error); } finally { saving.value = false; }
};

const handleTouchStart = (event) => {
  const touch = event.changedTouches[0];
  const edgeWidth = 32;
  if (touch.clientX < edgeWidth || touch.clientX > window.innerWidth - edgeWidth) {
    touchStart = null;
    return;
  }

  touchStart = {x: touch.clientX, y: touch.clientY};
};

const handleTouchEnd = (event) => {
  if (touchStart === null) {
    return;
  }

  const touch = event.changedTouches[0];
  const horizontalDistance = touch.clientX - touchStart.x;
  const verticalDistance = touch.clientY - touchStart.y;
  touchStart = null;
  if (Math.abs(horizontalDistance) < 48 || Math.abs(horizontalDistance) <= Math.abs(verticalDistance)) {
    return;
  }

  const currentIndex = categories.value.findIndex((category) => categoryKey(category) === selectedCategoryKey.value);
  const nextIndex = horizontalDistance < 0 ? currentIndex + 1 : currentIndex - 1;
  const nextCategory = categories.value[nextIndex];
  if (nextCategory !== undefined) {
    selectCategory(nextCategory);
  }
};

const handleWindowScroll = () => {
  currentState.value.scrollTop = window.scrollY;
  const remainingHeight = document.documentElement.scrollHeight - (window.scrollY + window.innerHeight);
  if (remainingHeight <= 200) {
    loadCategory(selectedCategory.value, true);
  }
};

const startListening = async () => {
  await loadCategory(selectedCategory.value);
  startUpdatePolling();
  window.addEventListener('scroll', handleWindowScroll, {passive: true});
  await nextTick();
  await scrollSelectedCategoryIntoView('auto');
  window.scrollTo({top: currentState.value.scrollTop, behavior: 'instant'});
};

const stopListening = () => {
  stopUpdatePolling();
  currentState.value.scrollTop = window.scrollY;
  window.removeEventListener('scroll', handleWindowScroll);
};

const restoreListPosition = async () => {
  await nextTick();
  window.scrollTo({top: currentState.value.scrollTop, behavior: 'instant'});
};

defineExpose({restoreListPosition, applyChatUpdate, stopUpdatePolling});

watch(() => props.highlightedPublicCustomerId, (id) => {
  activeHighlightedId.value = id === null ? null : String(id);
  if (highlightTimer !== null) {
    window.clearTimeout(highlightTimer);
  }
  if (activeHighlightedId.value !== null) {
    highlightTimer = window.setTimeout(() => { activeHighlightedId.value = null; }, 750);
  }
});

onMounted(() => {
  startListening();

  if (activeHighlightedId.value !== null) {
    highlightTimer = window.setTimeout(() => {
      activeHighlightedId.value = null;
    }, 750);
  }
});

onActivated(startListening);
onDeactivated(stopListening);

onBeforeUnmount(() => {
  stopListening();
  sortable?.destroy();
  if (highlightTimer !== null) {
    window.clearTimeout(highlightTimer);
  }
});
</script>

<style scoped>
.chat-list {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  color: #303036;
  background: #fff;
}

:global(.chat-list-page .ec-layoutRole__main.ec-layoutRole__main--subpage) {
  padding-top: 0 !important;
}

.chat-list__ribbon {
  position: sticky;
  z-index: 20;
  top: 42px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 48px;
  align-items: stretch;
  border-bottom: 1px solid #e5e5e8;
  background: #fff;
  box-shadow: 0 4px 12px rgba(34, 34, 40, 0.04);
}

.chat-list__search{display:flex;align-items:center;gap:10px;width:80%;margin:10px auto;padding:0 10px 0 14px;border:1px solid #d9d9dd;border-radius:22px;background:#fff}.chat-list__search input{width:100%;height:42px;border:0;outline:0}.chat-list__search button{display:grid;flex:0 0 32px;width:32px;height:32px;border:0;border-radius:50%;color:#666;background:transparent;place-items:center}.chat-list__search button:disabled{visibility:hidden}.chat-list-menu,.chat-category-modal,.chat-category-confirm{position:fixed;z-index:2050;inset:0;background:rgba(0,0,0,.35)}.chat-list-menu__panel{position:absolute;top:96px;right:max(16px,calc((100vw - 900px)/2));display:grid;width:260px;border-radius:10px;background:#fff;box-shadow:0 8px 30px rgba(0,0,0,.18);overflow:hidden}.chat-list-menu__panel button,.chat-category-modal__menu button,.chat-category-edit>div>button{min-height:50px;padding:10px 18px;border:0;border-bottom:1px solid #eee;background:#fff;text-align:left}.chat-category-modal,.chat-category-confirm{display:grid;padding:20px;place-items:center}.chat-category-modal__panel{width:min(520px,100%);max-height:85vh;border-radius:14px;background:#fff;overflow:auto}.chat-category-modal__panel header{display:grid;align-items:center;min-height:58px;padding:0 8px;grid-template-columns:44px minmax(0,1fr) 44px;border-bottom:1px solid #eee}.chat-category-modal__panel h2{grid-column:2;margin:0;font-size:18px;text-align:center}.chat-category-modal__panel header button{display:grid;width:44px;height:44px;border:0;background:transparent;font-size:18px;place-items:center}.chat-category-modal__back{grid-column:1;grid-row:1}.chat-category-modal__header-spacer{grid-column:1}.chat-category-modal__close{grid-column:3;grid-row:1}.chat-category-modal__body{padding:16px}.chat-category-modal__menu{display:grid}.chat-category-modal form{display:grid;gap:16px}.chat-category-modal label{display:grid;gap:7px}.chat-category-modal input[type=text],.chat-category-modal input:not([type]){height:42px;padding:0 12px;border:1px solid #ccc;border-radius:6px}.chat-category-modal__primary{min-height:44px;border:0;border-radius:22px;color:#fff;background:#f28b22}.chat-category-modal__footer{display:flex;justify-content:flex-end;padding-top:2px}.chat-category-modal__primary--standard{width:auto;min-width:120px;border-radius:4px}.chat-category-sortable{display:grid;gap:6px;margin-bottom:18px}.chat-category-sortable__row{display:grid;grid-template-columns:32px minmax(0,1fr) 32px;align-items:center;min-height:46px;padding:0 8px;border:1px solid #eee;border-radius:7px}.chat-category-sortable__handle{cursor:grab}.chat-category-edit{display:grid;gap:8px}.chat-category-edit>div{border:1px solid #eee;border-radius:7px;overflow:hidden}.chat-category-edit>div>button{width:100%}.chat-category-edit form{padding:12px}.chat-category-edit form div{display:grid;grid-template-columns:1fr 1fr;gap:8px}.chat-category-edit form button{min-height:40px;border:0;border-radius:20px}.chat-category-edit form button:first-child{color:#fff;background:#222}.chat-category-edit form button:last-child{color:#fff;background:#f28b22}.chat-category-confirm>div{display:grid;width:min(360px,100%);grid-template-columns:1fr 1fr;border-radius:12px;background:#fff;overflow:hidden}.chat-category-confirm p{grid-column:1/-1;margin:0;padding:28px 18px;text-align:center}.chat-category-confirm button{min-height:48px;border:0;border-top:1px solid #eee;background:#fff}.chat-category-help{text-align:center}.chat-category-help img{max-width:180px}.chat-list__member-help{display:block;margin:18px auto 92px;border:0;background:transparent;color:#555}.chat-list__member-actions{position:fixed;z-index:1000;right:0;bottom:0;left:0;display:grid;grid-template-columns:1fr 1fr;gap:10px;padding:12px max(16px,calc((100vw - 720px)/2));background:#fff;box-shadow:0 -4px 16px rgba(0,0,0,.12)}.chat-list__member-actions button{min-height:46px;border:1px solid #ddd;border-radius:23px;background:#fff}.chat-list__member-actions button:last-child{border:0;color:#fff;background:#f28b22}

.chat-list__categories {
  display: flex;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;
}

.chat-list__search input::-webkit-search-cancel-button {
  display: none;
}

.chat-category-modal__label {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.chat-category-modal__label small {
  color: #999;
  font-size: 12px;
  font-weight: 400;
}

.chat-category-sortable__row > span {
  cursor: pointer;
}

.chat-category-sortable__row > span:focus-visible {
  outline: 2px solid #2dbb69;
  outline-offset: 2px;
}

.chat-list__member-footer {
  position: fixed;
  z-index: 1000;
  right: 0;
  bottom: 0;
  left: 0;
  background: #fff;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.12);
}

.chat-list__member-help {
  width: 100%;
  min-height: 42px;
  margin: 0;
  border-bottom: 1px solid #eee;
}

.chat-list__member-actions {
  position: static;
  padding: 10px max(16px, calc((100vw - 720px) / 2)) 12px;
  box-shadow: none;
}

.chat-list__member-footer-spacer {
  height: 112px;
}

.chat-list__categories::-webkit-scrollbar {
  display: none;
}

.chat-list__category {
  position: relative;
  flex: 0 0 auto;
  min-width: 76px;
  min-height: 48px;
  padding: 12px 16px 11px;
  border: 0;
  color: #73737b;
  background: #fff;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
}

.chat-list__category--active {
  color: #238d50;
}

.chat-list__category--active::after {
  position: absolute;
  right: 14px;
  bottom: 0;
  left: 14px;
  height: 3px;
  border-radius: 3px 3px 0 0;
  background: #2dbb69;
  content: '';
}

.chat-list__menu {
  width: 48px;
  padding: 0;
  border: 0;
  border-left: 1px solid #ededf0;
  color: #606068;
  background: #fff;
  font-size: 20px;
}

.chat-list__category:focus-visible,
.chat-list__menu:focus-visible,
.chat-list__matching-button:focus-visible,
:deep(.chat-list-row:focus-visible) {
  outline: 2px solid #2dbb69;
  outline-offset: -2px;
}

.chat-list__matching-button:focus-visible {
  outline-color: #e87522;
}

.chat-list__items {
  min-width: 0;
}

.chat-list__content {
  position: relative;
  overflow-x: clip;
}

.chat-list__panel {
  width: 100%;
  min-width: 0;
  background: #fff;
}

.chat-list-slide-next-enter-active,
.chat-list-slide-next-leave-active,
.chat-list-slide-previous-enter-active,
.chat-list-slide-previous-leave-active {
  transition: transform 0.28s cubic-bezier(0.22, 0.61, 0.36, 1);
  will-change: transform;
}

.chat-list-slide-next-leave-active,
.chat-list-slide-previous-leave-active {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
}

.chat-list-slide-next-enter-from,
.chat-list-slide-previous-leave-to {
  transform: translateX(100%);
}

.chat-list-slide-next-leave-to,
.chat-list-slide-previous-enter-from {
  transform: translateX(-100%);
}

.chat-list__status,
.chat-list__more {
  padding: 32px 24px;
  color: #686870;
  text-align: center;
}

.chat-list__loading {
  display: grid;
  min-height: 320px;
  place-items: center;
}

.chat-list__more {
  padding: 18px 24px;
  font-size: 13px;
}

.chat-list__append-error {
  padding: 18px 24px 22px;
  color: #6d5b25;
  background: #fffaf0;
  font-size: 13px;
  text-align: center;
}

.chat-list__append-error p {
  margin: 0;
  line-height: 1.7;
}

.chat-list__append-error .fas {
  margin-right: 6px;
  color: #d39a17;
}

.chat-list__empty {
  display: grid;
  justify-items: center;
  padding: 88px 24px 40px;
  text-align: center;
}

.chat-list__empty p {
  margin: 0;
  color: #66666e;
  font-size: 15px;
  line-height: 1.8;
}

.chat-list__matching-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 18px;
  min-width: 190px;
  min-height: 44px;
  padding: 8px 20px;
  border-radius: 22px;
  color: #fff;
  background: #e87522;
  font-weight: 600;
  text-decoration: none;
}

.chat-menu-toggle { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.chat-menu-toggle__switch { position: relative; flex: 0 0 42px; width: 42px; height: 24px; border-radius: 999px; background: #c7c9cd; transition: background-color .15s; }
.chat-menu-toggle__switch::after { position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,.25); content: ""; transition: transform .15s; }
.chat-menu-toggle__switch--on { background: #2dbb69; }
.chat-menu-toggle__switch--on::after { transform: translateX(18px); }

@media (min-width: 768px) {
  .chat-list__ribbon {
    top: 54px;
  }
}

@media (max-width: 767px) {
  .chat-list {
    margin-right: -14px;
    margin-left: -14px;
    width: calc(100% + 28px);
  }
  .chat-list-menu__panel{top:auto;right:0;bottom:0;left:0;width:100%;border-radius:16px 16px 0 0}.chat-category-modal{padding:0;align-items:end}.chat-category-modal__panel{width:100%;max-height:88vh;border-radius:16px 16px 0 0}
}

@media (prefers-reduced-motion: reduce) {
  .chat-list-slide-next-enter-active,
  .chat-list-slide-next-leave-active,
  .chat-list-slide-previous-enter-active,
  .chat-list-slide-previous-leave-active {
    transition: none;
  }
}
</style>
